/**
 * FOLLOW-CAM (B4, spec §E.6 "Take the drive"). Pure math for the ride's camera.
 *
 *   s          distance travelled (m) = L · p(t)
 *   look-at    the point s + clamp(0.012·L, 250, 1500) m ahead (clamped to the pin)
 *   bearing    the travel bearing over the look-ahead chord, smoothed by a critically damped spring
 *              (half-life 700 ms: a step's error halves in exactly 700 ms), at most 45°/s, and never more
 *              than 90° from the travel direction, so the road always runs into the screen.
 */
import type { LngLat } from "@/contracts/data";
import { along, angleDelta, bearing as geoBearing } from "@/lib/geo";

export const BEARING_HALF_LIFE_MS = 700;
export const BEARING_MAX_RATE = 45; //   degrees per second
export const BEARING_MAX_DEV = 90; //    degrees from the travel direction

/** (1 + u)·e^(−u) = ½ → u ≈ 1.67835: the critically damped step response halves at t = u / ω. */
const HALF = 1.6783469900166612;

export interface Spring {
  value: number; //    degrees (unwrapped: may leave [0, 360))
  velocity: number; // degrees per ms
}

/** Wraps to [0, 360). */
export const wrap360 = (d: number): number => ((d % 360) + 360) % 360;

/**
 * One step of the bearing spring toward `goal` (degrees, any wrap) over dt ms. The spring is critically
 * damped; then the step is rate-limited to maxRate °/s and kept within maxDev° of `travel`.
 */
export function stepBearing(
  s: Spring,
  goal: number,
  dt: number,
  o: { halfLifeMs?: number; maxRate?: number; maxDev?: number; travel?: number } = {},
): Spring {
  const h = o.halfLifeMs ?? BEARING_HALF_LIFE_MS;
  const maxRate = (o.maxRate ?? BEARING_MAX_RATE) / 1000; // °/ms
  const maxDev = o.maxDev ?? BEARING_MAX_DEV;
  const t = Math.max(0, Math.min(dt, 250)); // a resumed tab must not fling the camera
  const g = s.value + angleDelta(s.value, goal); // the goal unwrapped next to the current value
  const w = HALF / Math.max(1, h);
  const j0 = s.value - g;
  const j1 = s.velocity + j0 * w;
  const e = Math.exp(-w * t);
  let value = g + e * (j0 + j1 * t);
  let velocity = e * (s.velocity - j1 * w * t);
  // rate limit
  const step = value - s.value;
  const lim = maxRate * t;
  if (Math.abs(step) > lim) {
    value = s.value + Math.sign(step) * lim;
    velocity = Math.sign(velocity) * Math.min(Math.abs(velocity), maxRate);
  }
  // never more than maxDev from the travel direction (the map is never flipped)
  const travel = o.travel ?? goal;
  const dev = angleDelta(travel, value);
  if (Math.abs(dev) > maxDev) {
    value = value - dev + Math.sign(dev) * maxDev;
    velocity = 0;
  }
  return { value, velocity };
}

/** The error ratio of the unclamped spring after t ms from rest (for tests: 0.5 at the half-life). */
export function springStepResponse(t: number, halfLifeMs = BEARING_HALF_LIFE_MS): number {
  const u = (HALF / halfLifeMs) * t;
  return (1 + u) * Math.exp(-u);
}

export interface RouteGeom {
  coords: LngLat[];
  cum: number[];
  L: number;
}

/** Travel bearing at s: the chord over the next `ahead` metres (the last `ahead` metres near the pin). */
export function chordBearing(r: RouteGeom, s: number, ahead: number): number {
  const lo = Math.max(0, Math.min(s, r.L - ahead));
  const hi = Math.min(r.L, lo + Math.max(1, ahead));
  const a = along(r.coords, r.cum, lo).lngLat;
  const b = along(r.coords, r.cum, hi).lngLat;
  if (a[0] === b[0] && a[1] === b[1]) return along(r.coords, r.cum, s).bearing;
  return geoBearing(a, b);
}

/** Where the camera looks at distance s (the look-ahead point, clamped to the pin). */
export function lookAt(r: RouteGeom, s: number, ahead: number): LngLat {
  return along(r.coords, r.cum, Math.min(r.L, Math.max(0, s) + ahead)).lngLat;
}

/** Where the comet is at distance s. */
export function pointAtM(r: RouteGeom, s: number): LngLat {
  return along(r.coords, r.cum, Math.max(0, Math.min(r.L, s))).lngLat;
}
